<template>
  <div class="single-video">
    <div v-show="!isComponentLoading" class="single-video__wrapper">
      <button @click="$emit('close-video-player')" class="single-video__close">
        <CloseIcon class="single-video__close--icon" />
      </button>
      <div id="player"></div>
    </div>
    <div v-show="isComponentLoading" class="single-video__spinner-wrapper">
      <ActionLoader loaderClass="single-video__spinner" />
    </div>
  </div>
</template>

<script>
import { createPlayer, sendKinesisHeartbeat } from '@/utils'
import { mapState } from 'vuex'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import CloseIcon from '@/assets/icons/navigation/close_icon.svg'
import { kinesisCommons } from '@/helpers/mixins'

export default {
  name: 'SingleVideo',
  components: {
    CloseIcon: { ...CloseIcon, name: 'CloseIcon' },
    ActionLoader,
  },
  mixins: [kinesisCommons],
  props: {
    videoId: {
      type: String,
      required: true,
    },
    trigger: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      instance: [],
      currentTime: 0,
      lastTime: 0,
      delta: 0,
      totalDuration: 0,
      videoFetched: null,
      videoPositionUpdated: true, // avoid first update
    }
  },
  computed: {
    ...mapState({
      jwVideo: state => state.content.jwVideo,
      isComponentLoading: state => state.app.isComponentLoading,
      profile: state => state.auth.userProfile,
      isImpersonating: state => state.admin.isImpersonating,
      course: state => state.courses.currentCourse,
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
  },
  methods: {
    setupPlayer(playlist) {
      this.instance = createPlayer({
        element: 'player',
        key: process.env.VUE_APP_JWPLAYER_KEY,
        playlist,
        events: {
          ready: () => this.sendAllHeartbeats(),
          play: () => {
            this.totalDuration = this.instance.getDuration()
            this.sendAllHeartbeats()
          },
          pause: () => this.sendAllHeartbeats(),
          complete: () => {
            const isCompleted = true
            this.sendAllHeartbeats()
            this.updateVideoPosition(isCompleted)
            this.$emit('update-status')
          },
          error: () => this.sendAllHeartbeats(e),
          time: () => {
            this.currentTime = parseInt(this.instance.getPosition())
            if (this.currentTime % 5 || this.currentTime === 0) {
              if (this.lastTime !== this.currentTime) {
                this.delta++
              }
              this.lastTime = this.currentTime // Fix for JWPlayer polling multiple times
            } else {
              if (this.lastTime !== this.currentTime) {
                this.delta++
                this.sendAllHeartbeats()
                this.delta = 0
                this.videoPositionUpdated = false
              }
              this.lastTime = this.currentTime // Fix for JWPlayer polling multiple times
            }
            if (this.currentTime === parseInt(this.totalDuration) - 30) {
              this.$emit('update-status')
            }
            if (
              this.currentTime % process.env.VUE_APP_RESUME_VIDEO_DELTA === 0 &&
              !this.videoPositionUpdated
            ) {
              this.videoPositionUpdated = true
              this.updateVideoPosition()
            }
          },
        },
      })
    },
    async sendAllHeartbeats() {
      if (!this.isImpersonating) {
        const {
          delta,
          profile: { uaaId: user_id, account_id, team_ids },
          videoId: video_id,
        } = this
        const position = parseInt(this.instance.getPosition())
        const parent_data = this.parentData
        sendKinesisHeartbeat({
          position,
          delta,
          user_id,
          account_id,
          team_ids,
          video_id,
          parent_data,
        })
      }
    },
    async updateVideoPosition(isCompleted = false) {
      const position = parseInt(this.instance.getPosition())
      const roundedPosition = isCompleted
        ? this.videoFetched.duration_in_seconds
        : 0
      const currentPosition = roundedPosition || position
      await this.$store.dispatch('content/updateVideoPosition', {
        videoId: this.videoId,
        position: currentPosition,
      })
    },
  },
  async created() {
    this.$store.dispatch('app/setIsComponentLoading', true)
    await this.$store.dispatch('content/getJWVideo', this.videoId)
    const { playlist: sources } = this.jwVideo
    if (sources) {
      this.setupPlayer(sources)
      if (this.isAuthenticated) {
        this.videoFetched = await this.$store.dispatch(
          'content/getVideoStatus',
          this.videoId
        )
        const isVideoCompleted =
          this.videoFetched.current_position_in_secs ===
          this.videoFetched.duration_in_seconds

        if (isVideoCompleted) {
          this.$store.dispatch('content/updateVideoPosition', {
            videoId: this.videoId,
            position: 0,
          })
        }
        if (process.env.VUE_APP_RESUME_VIDEO_ENABLED) {
          const videoPosition = isVideoCompleted
            ? 0
            : this.videoFetched.current_position_in_secs
          this.instance?.seek(videoPosition)
        }
      }
    } else {
      this.$emit('close-video-player')
    }
    this.$store.dispatch('app/setIsComponentLoading', false)
    this.sendMixpanelEvent(this.eventsConstants.CONTENT_VIDEO_MEDIA_REQUESTED, {
      course_id: this.course ? this.course.id : '',
      course_name: this.course ? this.course.name : '',
      video_id: this.videoId,
      video_name: this.videoFetched.name,
      trigger: this.trigger,
      ...this.videoFetched,
    })
  },
  destroyed() {
    document.querySelector('body').style.overflowY = 'visible'
    if (this.instance) this.instance.remove()
  },
}
</script>

<style lang="scss" scoped>
.single-video {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
  width: 100%;
  height: 100%;
  background-color: var(--black-90);
  transition: opacity 0.3s ease;
  &__wrapper {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    top: 15%;
    margin: 0px auto;
    background-color: var(--light-gray);
    color: var(--white);
    max-width: 50vw;
    min-height: 50vh;
  }
  &__close {
    width: 50px;
    height: 50px;
    position: absolute;
    right: -20px;
    top: -50px;
    background: none;
    border: none;
    cursor: pointer;
    &:hover {
      .single-video__close--icon {
        fill: var(--orange);
      }
    }
    &--icon {
      fill: var(--white);
    }
  }
  &__spinner-wrapper {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
